import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useStoreSettings } from '../hooks/useStoreSettings';
import SeoHead from '../components/seo/SeoHead';
import { sanitize } from '../lib/sanitize';

interface ProductLite {
  id: string;
  name: string;
  slug: string | null;
  image_url: string | null;
  price: number | null;
  short_description: string | null;
}

interface NavItem {
  id: string;
  title: string;
  url: string;
  is_active: boolean;
  selected_products?: string[];
  meta_title?: string;
  meta_description?: string;
}

const useFormatPrice = () => {
  const { settings } = useStoreSettings();
  const symbol = (settings as any)?.default_currency_symbol || 'RSD';
  return (price: number) =>
    `${new Intl.NumberFormat('sr-RS', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(price)} ${symbol}`;
};

const NavItemPage: React.FC = () => {
  const { settings } = useStoreSettings();
  const location = useLocation();
  const formatPrice = useFormatPrice();

  const [products, setProducts] = useState<ProductLite[]>([]);
  const [loading, setLoading] = useState(true);

  const navItems: NavItem[] =
    ((settings?.header_content as any)?.navigation as NavItem[]) || [];
  const currentItem = navItems.find(
    n => n.url === location.pathname || n.url === location.pathname.replace(/\/$/, '')
  );

  // Fallback: footer quick_link with custom page content matching this URL.
  const quickLinks: Array<{ title: string; url: string; content?: string }> =
    ((settings?.footer_content as any)?.quick_links as Array<any>) || [];
  const currentQuickLink = !currentItem
    ? quickLinks.find(
        q =>
          q.url === location.pathname || q.url === location.pathname.replace(/\/$/, ''),
      )
    : undefined;

  useEffect(() => {
    const ids = currentItem?.selected_products || [];
    if (!currentItem || ids.length === 0) {
      setProducts([]);
      setLoading(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('products')
        .select('id, name, slug, image_url, price, short_description')
        .in('id', ids)
        .eq('is_active', true);
      if (data) {
        // Preserve admin-defined order from selected_products.
        const order = new Map(ids.map((id, i) => [id, i]));
        const sorted = [...data].sort(
          (a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0)
        );
        setProducts(sorted as ProductLite[]);
      }
      setLoading(false);
    })();
  }, [currentItem?.id, JSON.stringify(currentItem?.selected_products)]);

  if (!currentItem && currentQuickLink && currentQuickLink.content) {
    return (
      <div style={{ padding: '2rem 1rem', maxWidth: 860, margin: '0 auto' }}>
        <SeoHead title={currentQuickLink.title} />
        <h1 style={{ marginBottom: '1.5rem' }}>{currentQuickLink.title}</h1>
        <div
          style={{ lineHeight: 1.7, color: '#1f2937' }}
          dangerouslySetInnerHTML={{ __html: sanitize(currentQuickLink.content) }}
        />
      </div>
    );
  }

  if (!currentItem) {
    return (
      <div style={{ padding: '4rem 1rem', textAlign: 'center' }}>
        <h1>Stranica nije pronađena</h1>
        <p>Putanja {location.pathname} ne postoji.</p>
        <Link to="/">Nazad na početnu</Link>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 1rem', maxWidth: 1200, margin: '0 auto' }}>
      <SeoHead
        title={currentItem.meta_title || currentItem.title}
        description={currentItem.meta_description}
      />
      <h1 style={{ marginBottom: '1.5rem' }}>{currentItem.title}</h1>

      {loading ? (
        <p style={{ minHeight: '100vh' }}>Učitavanje proizvoda...</p>
      ) : products.length === 0 ? (
        <p style={{ color: '#6b7280' }}>
          Trenutno nema proizvoda u ovoj sekciji. Dodaj ih u admin panelu pod{' '}
          <Link to="/admin/settings/store#header">Header → {currentItem.title}</Link>.
        </p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 24,
          }}
        >
          {products.map(p => (
            <Link
              key={p.id}
              to={`/p/${p.slug || p.id}`}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                overflow: 'hidden',
                background: '#fff',
                transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={e => ((e.currentTarget as HTMLElement).style.boxShadow = '0 4px 14px rgba(0,0,0,0.08)')}
              onMouseLeave={e => ((e.currentTarget as HTMLElement).style.boxShadow = 'none')}
            >
              <div style={{ aspectRatio: '1 / 1', background: '#f9fafb' }}>
                {p.image_url && (
                  <img
                    src={p.image_url}
                    alt={p.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </div>
              <div style={{ padding: 12 }}>
                <h3 style={{ fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>{p.name}</h3>
                {p.price != null && (
                  <div style={{ color: 'var(--accent-color)', fontWeight: 700 }}>{formatPrice(p.price)}</div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default NavItemPage;
